// Last-known-good snapshot of StringsSG personal training rate benchmarks.
//
// WHY THIS FILE EXISTS
// The rates guide (/sg/personal-trainer/personal-trainer-rates-singapore) fetches
// live aggregates at build time from /public/GetFitnessRateBenchmarks. If that call
// fails the page must still render, so this committed snapshot is the fallback. It is
// also the thing that makes the guide citable: every competing page asserts ranges,
// none of them can state a median over a known number of real bookings.
//
// HOW TO REFRESH IT
// Re-call the endpoint and paste the response below. Do NOT hand-edit the figures:
// the entire value of the section is that the numbers are real and the sample size is
// stated. An invented median here is worse than no section, because it is exactly the
// claim people would cite us for. Setting ready: false hides the section entirely.
//
// Captured from production on 21 September 2026.
//
// PRIVACY: aggregates only. No per-trainer or per-customer rows belong in this file.

export const ptRateBenchmarksSnapshot = {
    ready: true,

    // ISO instant the aggregates were computed.
    asOf: "2026-09-21T06:14:49.447917448Z",

    // Rolling 12-month window the aggregates cover.
    windowStart: "2025-09-21",
    windowEnd: "2026-09-21",

    // Headline figures, in SGD per session. Solo (1-on-1) bookings only - a per-person
    // buddy rate is not comparable, so it is reported separately rather than blended in.
    // sampleSize is the number of separately priced packages - one negotiated price
    // each, so it is the statistical sample size, and the ONLY figure the medians are
    // computed from. estimatedTotalPackages and totalSessions describe scale; neither
    // feeds a median.
    sampleSize: 84,
    // ESTIMATE, not a measurement: 84 priced packages x the configured manual-closure
    // multiplier of 3. Describes business volume only and never feeds the medians.
    // Refresh from the API rather than recomputing by hand.
    estimatedTotalPackages: 252,
    // Zero until the backend carrying totalSessions is deployed and this snapshot is
    // recaptured. Do not estimate it from byPackageSize buckets - that is arithmetic on
    // ranges, not a measurement. While it is 0 the page leads with the package count
    // instead, which is a figure we actually have.
    totalSessions: 0,
    medianPerSession: 86,
    p25PerSession: 70,
    p75PerSession: 94,

    // Breakdowns. Each row: { label, median, p25, p75, n }
    byPackageSize: [
        { label: "30-49 sessions", median: 82, p25: 68, p75: 90, n: 34 },
        { label: "10-19 sessions", median: 94, p25: 86, p75: 96, n: 22 },
        { label: "20-29 sessions", median: 94, p25: 85, p75: 94, n: 14 },
        { label: "1-9 sessions", median: 70, p25: 50, p75: 90, n: 14 },
    ],

    // Empty in this capture. The tier prefix is only written by the package-picker
    // booking flow, so the other flows leave it unset and every tier group falls below
    // the minimum sample of 5 and is suppressed. Expected to populate as volume grows.
    byTier: [],

    // Not rendered. The endpoint returns six districts at n=7-12, which is too thin for
    // a median to mean anything, so the page carries a qualitative note instead. Left
    // empty here rather than carrying data nothing displays - re-populate from the API
    // once a district clears roughly 30 packages.
    byDistrict: [],

    // Buddy training, per person. Same row shape, or null when there were too few
    // buddy bookings to publish.
    buddy: { label: "Buddy training, per person", median: 73, p25: 67, p75: 74, n: 6 },

    // Transparency counters. The backend suppresses rather than guesses, and these say
    // how much was set aside, so the methodology note can be honest about it.
    excludedCount: 0,                  // per-session rate outside the sanity band
    addressesWithoutPostalCode: 14,    // no usable postal code, so absent from byDistrict
}

// The page renders the benchmark section only when this returns true, so an empty or
// partially filled snapshot silently degrades to the existing market-range content
// instead of publishing blanks or nulls.
export const hasBenchmarks = (benchmarks) =>
    Boolean(
        benchmarks &&
        benchmarks.ready &&
        benchmarks.sampleSize > 0 &&
        benchmarks.medianPerSession > 0 &&
        benchmarks.windowStart &&
        benchmarks.windowEnd
    )

// Formats the window as "October 2025 and September 2026" for the on-page sentence.
export const formatBenchmarkWindow = (benchmarks) => {
    if (!benchmarks?.windowStart || !benchmarks?.windowEnd) return null
    const fmt = (value) => {
        const d = new Date(value)
        if (isNaN(d.getTime())) return null
        return d.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' })
    }
    const start = fmt(benchmarks.windowStart)
    const end = fmt(benchmarks.windowEnd)
    if (!start || !end) return null
    return `${start} and ${end}`
}

// The API returns asOf as a full instant with nanosecond precision, which reads as
// machine output on the page. Render it as a plain date instead.
export const formatBenchmarkAsOf = (benchmarks) => {
    if (!benchmarks?.asOf) return null
    const d = new Date(benchmarks.asOf)
    if (isNaN(d.getTime())) return null
    return d.toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' })
}
